import {
  formatPrice,
  isUzMobilePhone,
  OrderCategory,
  ORDER_PHOTOS_MAX,
  ORDER_PHOTOS_MIN,
  SizeClass,
  TEXT_LIMITS,
  values,
  type CreateOrderResponse,
  type Language,
} from '@posilka/shared';
import { copyTextToClipboard, shareURL } from '@telegram-apps/sdk-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { ApiError } from '@posilka/client/api/client';
import {
  isValuable,
  useCities,
  useCreateOrder,
  usePriceSuggestion,
} from '@posilka/client/api/orders';
import { CitySelect } from '@/components/CitySelect';
import { cityName } from '@posilka/client/lib/cities';
import { Field } from '@/components/Field';
import { MainAction } from '@/components/MainAction';
import { PhoneInput } from '@/components/PhoneInput';
import { TripPicker } from '@/features/new-order/TripPicker';
import { SavedAddressBar, SavedRecipientBar } from '@/features/saved/SavedBar';
import { PhotoPicker } from '@/components/PhotoPicker';
import { PriceInput } from '@/components/PriceInput';
import { Row, Section } from '@/components/Section';
import { ErrorState, ListSkeleton } from '@/components/States';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { LocationPicker } from '@/features/map/LocationPicker';
import { useDraft } from '@/features/new-order/draft';
import { haptic } from '@/lib/telegram';
import {
  addDays,
  formatWindow,
  fromTashkent,
  nextHourTashkent,
  todayTashkent,
  toTashkent,
} from '@posilka/client/lib/time';
import { MissingList } from '@/components/MissingList';

const STEPS = ['route', 'pickup', 'dropoff', 'item', 'price', 'confirm'] as const;
const CATEGORY_ICON: Record<OrderCategory, string> = {
  DOCUMENT: '📄',
  PHONE: '📱',
  ELECTRONICS: '💻',
  KEYS: '🔑',
  SMALL_PACKAGE: '📦',
  OTHER: '🎁',
};

export function NewOrderPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const navigate = useNavigate();
  const draft = useDraft();
  const cities = useCities();
  const price = usePriceSuggestion(draft.fromCityId, draft.toCityId, draft.sizeClass);
  const create = useCreateOrder();
  const errorMessage = useErrorMessage();
  const [result, setResult] = useState<CreateOrderResponse | null>(null);
  const [openedAt] = useState(() => Date.now());

  const cityById = useMemo(() => new Map((cities.data ?? []).map((c) => [c.id, c])), [cities.data]);
  const from = draft.fromCityId ? cityById.get(draft.fromCityId) : undefined;
  const to = draft.toCityId ? cityById.get(draft.toCityId) : undefined;

  // Narx tavsiyasi o'zgarsa (yo'nalish/o'lcham), narxni oraliqqa qaytaramiz.
  useEffect(() => {
    const p = price.data;
    if (!p) return;
    if (draft.price === undefined || draft.price < p.min || draft.price > p.max) {
      draft.set({ price: p.suggestedPrice });
    }
  }, [price.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (result) return <SuccessStep result={result} />;
  if (cities.isPending) return <ListSkeleton />;
  if (cities.isError)
    return <ErrorState error={cities.error} onRetry={() => void cities.refetch()} />;

  const step = STEPS[draft.step] ?? 'route';
  const today = todayTashkent();
  const windowIso =
    draft.date && draft.timeFrom && draft.timeTo
      ? {
          from: fromTashkent(draft.date, draft.timeFrom),
          to: fromTashkent(draft.date, draft.timeTo),
        }
      : null;

  // Har qadam shartlari: [bajarildimi, foydalanuvchiga tushuntirish]. `valid` — hammasi bajarilganmi;
  // bajarilmaganlari "Davom etish uchun" ro'yxatida ko'rsatiladi (tugma nega o'chiqligi tushunarli bo'lsin).
  const fmt = (n: number) => formatPrice(n, t('common.currency'));
  const checks: Record<(typeof STEPS)[number], [boolean, string | null][]> = {
    route: [
      [!!from && !!to && from.id !== to.id, t('order.rule.route')],
      [!price.isError, null], // "Qayerga" maydoni ostida ROUTE_NOT_SUPPORTED ko'rsatiladi.
    ],
    pickup: [
      [
        (draft.pickup?.address.trim().length ?? 0) >= TEXT_LIMITS.address.min,
        t('order.rule.pickupAddress', { min: TEXT_LIMITS.address.min }),
      ],
      [
        !!windowIso && windowIso.to > windowIso.from && new Date(windowIso.to).getTime() > openedAt,
        t('order.rule.pickupWindow'),
      ],
    ],
    dropoff: [
      [
        (draft.dropoff?.address.trim().length ?? 0) >= TEXT_LIMITS.address.min,
        t('order.rule.dropoffAddress', { min: TEXT_LIMITS.address.min }),
      ],
      [
        draft.recipientName.trim().length >= TEXT_LIMITS.recipientName.min,
        t('order.rule.recipientName', { min: TEXT_LIMITS.recipientName.min }),
      ],
      [isUzMobilePhone(`+998${draft.recipientPhone}`), t('order.rule.recipientPhone')],
    ],
    item: [
      [!!draft.category, t('order.rule.category')],
      [!!draft.sizeClass, t('order.rule.sizeClass')],
      [
        draft.photos.length >= ORDER_PHOTOS_MIN,
        t('order.rule.photos', { min: ORDER_PHOTOS_MIN, max: ORDER_PHOTOS_MAX }),
      ],
    ],
    price: [
      [
        !!price.data &&
          draft.price !== undefined &&
          draft.price >= price.data.min &&
          draft.price <= price.data.max,
        price.data
          ? t('order.rule.price', { min: fmt(price.data.min), max: fmt(price.data.max) })
          : null,
      ],
    ],
    confirm: [[draft.confirmed, t('order.rule.confirm')]],
  };
  const valid = Object.fromEntries(STEPS.map((s) => [s, checks[s].every(([ok]) => ok)])) as Record<
    (typeof STEPS)[number],
    boolean
  >;
  const missing = checks[step].filter(([ok, why]) => !ok && why).map(([, why]) => why!);

  const next = () => {
    haptic.tap();
    if (step !== 'confirm') {
      // Joy tanlash qadamiga kirganda default — shahar markazi.
      if (step === 'route' && from && !draft.pickup)
        draft.set({ pickup: { address: '', lat: from.lat, lng: from.lng } });
      if (step === 'pickup' && to && !draft.dropoff)
        draft.set({ dropoff: { address: '', lat: to.lat, lng: to.lng } });
      draft.set({ step: draft.step + 1 });
      window.scrollTo(0, 0);
      return;
    }
    create.mutate(
      {
        fromCityId: draft.fromCityId,
        toCityId: draft.toCityId,
        pickupAddress: draft.pickup!.address.trim(),
        pickupLat: draft.pickup!.lat,
        pickupLng: draft.pickup!.lng,
        pickupFrom: windowIso!.from,
        pickupTo: windowIso!.to,
        dropoffAddress: draft.dropoff!.address.trim(),
        dropoffLat: draft.dropoff!.lat,
        dropoffLng: draft.dropoff!.lng,
        recipientName: draft.recipientName.trim(),
        recipientPhone: `+998${draft.recipientPhone}`,
        category: draft.category,
        sizeClass: draft.sizeClass,
        description: draft.description.trim(),
        price: draft.price,
        photoKeys: draft.photos.map((p) => p.key),
        noProhibitedItems: true,
        ...(draft.tripId && { tripId: draft.tripId }),
      },
      {
        onSuccess: (res) => {
          haptic.success();
          draft.reset();
          setResult(res);
        },
        onError: () => haptic.error(),
      },
    );
  };

  let body: ReactNode;
  switch (step) {
    case 'route':
      body = (
        <>
          <Field label={t('order.from')}>
            <CitySelect
              cities={cities.data}
              value={draft.fromCityId}
              exclude={draft.toCityId}
              onChange={(id) => draft.set({ fromCityId: id, pickup: undefined, tripId: undefined })}
            />
          </Field>
          <div className="-my-1 flex justify-center">
            <button
              type="button"
              aria-label={t('order.swap')}
              className="press grid h-10 w-10 place-items-center rounded-full border border-line bg-surface text-lg font-bold text-accent"
              onClick={() =>
                draft.set({
                  fromCityId: draft.toCityId,
                  toCityId: draft.fromCityId,
                  pickup: undefined,
                  dropoff: undefined,
                  tripId: undefined,
                })
              }
            >
              ⇅
            </button>
          </div>
          <Field
            label={t('order.to')}
            error={price.isError && draft.toCityId ? t('errors.ROUTE_NOT_SUPPORTED') : null}
          >
            <CitySelect
              cities={cities.data}
              value={draft.toCityId}
              exclude={draft.fromCityId}
              onChange={(id) => draft.set({ toCityId: id, dropoff: undefined, tripId: undefined })}
            />
          </Field>
          <TripPicker
            fromCityId={draft.fromCityId}
            toCityId={draft.toCityId}
            value={draft.tripId}
            onChange={(trip) => {
              if (!trip) return draft.set({ tripId: undefined });
              // Olish oynasi — jo'nashdan 2 soat oldin (bugun bo'lsa, hozirgi soatdan) jo'nashgacha.
              const depart = toTashkent(trip.departAt);
              const startIso = new Date(
                Math.max(Date.parse(trip.departAt) - 2 * 3600_000, Date.now() + 5 * 60_000),
              ).toISOString();
              const start = toTashkent(startIso);
              draft.set({
                tripId: trip.id,
                date: depart.date,
                timeFrom: start.date === depart.date ? start.time : '00:00',
                timeTo: depart.time,
              });
            }}
          />
        </>
      );
      break;

    case 'pickup':
      body = (
        <>
          <Field
            label={t('order.pickupPlace', { city: from ? cityName(from, lang) : '' })}
            count={{ value: draft.pickup?.address ?? '', ...TEXT_LIMITS.address }}
          >
            <SavedAddressBar
              cityId={from!.id}
              value={draft.pickup}
              onPick={(pickup) => draft.set({ pickup })}
            />
            <LocationPicker
              value={draft.pickup ?? { address: '', lat: from!.lat, lng: from!.lng }}
              center={from!}
              onChange={(pickup) => draft.set({ pickup })}
            />
          </Field>
          <Field label={t('order.pickupDate')}>
            <div className="mb-2 flex gap-2">
              {[0, 1, 2].map((d) => {
                const date = addDays(today, d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => {
                      // Bugun uchun default oyna — keyingi soatdan 4 soat.
                      if (d === 0) {
                        const start = nextHourTashkent();
                        const end = `${String(Math.min(23, Number(start.slice(0, 2)) + 4)).padStart(2, '0')}:59`;
                        draft.set({ date, timeFrom: start, timeTo: end });
                      } else draft.set({ date });
                    }}
                    className={`press flex-1 rounded-full py-2.5 text-sm font-semibold ${
                      draft.date === date
                        ? 'border border-lime bg-lime font-semibold text-lime-ink'
                        : 'border border-line bg-surface'
                    }`}
                  >
                    {t(`order.day${d}`)}
                  </button>
                );
              })}
            </div>
            <input
              type="date"
              className="input"
              min={today}
              max={addDays(today, 13)}
              value={draft.date ?? ''}
              onChange={(e) => draft.set({ date: e.target.value })}
            />
          </Field>
          <Field
            label={t('order.pickupWindow')}
            error={
              !windowIso
                ? null
                : windowIso.to <= windowIso.from
                  ? t('order.windowInvalid')
                  : new Date(windowIso.to).getTime() <= openedAt
                    ? t('order.windowPast')
                    : null
            }
          >
            <div className="flex items-center gap-2">
              <input
                type="time"
                className="input"
                value={draft.timeFrom}
                onChange={(e) => draft.set({ timeFrom: e.target.value })}
              />
              <span className="text-hint">—</span>
              <input
                type="time"
                className="input"
                value={draft.timeTo}
                onChange={(e) => draft.set({ timeTo: e.target.value })}
              />
            </div>
          </Field>
        </>
      );
      break;

    case 'dropoff':
      body = (
        <>
          <Field
            label={t('order.dropoffPlace', { city: to ? cityName(to, lang) : '' })}
            count={{ value: draft.dropoff?.address ?? '', ...TEXT_LIMITS.address }}
          >
            <SavedAddressBar
              cityId={to!.id}
              value={draft.dropoff}
              onPick={(dropoff) => draft.set({ dropoff })}
            />
            <LocationPicker
              value={draft.dropoff ?? { address: '', lat: to!.lat, lng: to!.lng }}
              center={to!}
              onChange={(dropoff) => draft.set({ dropoff })}
            />
          </Field>
          <SavedRecipientBar
            name={draft.recipientName}
            phone={draft.recipientPhone}
            onPick={(recipientName, recipientPhone) => draft.set({ recipientName, recipientPhone })}
          />
          <Field
            label={t('order.recipientName')}
            count={{ value: draft.recipientName, ...TEXT_LIMITS.recipientName }}
          >
            <input
              className="input"
              value={draft.recipientName}
              maxLength={TEXT_LIMITS.recipientName.max}
              autoComplete="name"
              onChange={(e) => draft.set({ recipientName: e.target.value })}
            />
          </Field>
          <Field label={t('order.recipientPhone')} hint={t('order.recipientPhoneHint')}>
            <PhoneInput
              value={draft.recipientPhone}
              onChange={(recipientPhone) => draft.set({ recipientPhone })}
            />
          </Field>
        </>
      );
      break;

    case 'item':
      body = (
        <>
          <Field label={t('order.category')}>
            <div className="grid grid-cols-3 gap-2">
              {values(OrderCategory).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => draft.set({ category: c })}
                  className={`press flex flex-col items-center gap-1.5 rounded-2xl px-2 py-3 text-xs font-semibold ${
                    draft.category === c
                      ? 'border border-lime bg-lime font-semibold text-lime-ink'
                      : 'border border-line bg-surface'
                  }`}
                >
                  <span className="text-2xl">{CATEGORY_ICON[c]}</span>
                  {t(`category.${c}`)}
                </button>
              ))}
            </div>
          </Field>
          <Field label={t('order.size')}>
            <div className="space-y-2">
              {values(SizeClass).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => draft.set({ sizeClass: s })}
                  className={`press flex w-full items-center justify-between gap-3 rounded-2xl px-4 py-3.5 text-left ${
                    draft.sizeClass === s
                      ? 'border border-lime bg-lime font-semibold text-lime-ink'
                      : 'border border-line bg-surface'
                  }`}
                >
                  <span className="font-bold">{t(`size.${s}.title`)}</span>
                  <span className="text-right text-sm opacity-70">{t(`size.${s}.hint`)}</span>
                </button>
              ))}
            </div>
          </Field>
          {isValuable(draft.category, draft.sizeClass) && (
            <p className="card mb-3 border-amber/40 px-5 py-4 text-sm">
              ⚠️ {t('order.valuableWarning')}
            </p>
          )}
          <Field
            label={t('order.description')}
            count={{ value: draft.description, ...TEXT_LIMITS.orderDescription }}
          >
            <textarea
              className="input min-h-20 resize-none"
              maxLength={TEXT_LIMITS.orderDescription.max}
              placeholder={t('order.descriptionPlaceholder')}
              value={draft.description}
              onChange={(e) => draft.set({ description: e.target.value })}
            />
          </Field>
          <Field label={t('order.photos')} hint={t('order.photosHint')}>
            <PhotoPicker
              purpose="order_photo"
              value={draft.photos}
              onChange={(photos) => draft.set({ photos })}
            />
          </Field>
        </>
      );
      break;

    case 'price': {
      const p = price.data;
      body = !p ? (
        price.isError ? (
          <ErrorState error={price.error} />
        ) : (
          <ListSkeleton rows={1} />
        )
      ) : (
        <>
          <div className="card glow mb-5 flex items-center justify-between gap-3 p-5">
            <span className="text-sm text-muted">{t('order.suggestedPrice')}</span>
            <span className="font-display text-xl font-bold whitespace-nowrap text-accent">
              {formatPrice(p.suggestedPrice, t('common.currency'))}
            </span>
          </div>
          <Field
            label={t('order.yourPrice')}
            hint={t('order.priceRange', {
              min: formatPrice(p.min, t('common.currency')),
              max: formatPrice(p.max, t('common.currency')),
            })}
          >
            <PriceInput
              value={draft.price}
              onChange={(price) => draft.set({ price })}
              min={p.min}
              max={p.max}
              currency={t('common.currency')}
              invalid={!valid.price}
              label={t('order.yourPrice')}
            />
            {!valid.price && (
              <p className="mb-2 px-1 text-sm text-destructive">
                {t('order.priceRange', {
                  min: formatPrice(p.min, t('common.currency')),
                  max: formatPrice(p.max, t('common.currency')),
                })}
              </p>
            )}
            <input
              type="range"
              className="w-full accent-[var(--lime)]"
              min={p.min}
              max={p.max}
              step={1000}
              value={draft.price ?? p.suggestedPrice}
              onChange={(e) => draft.set({ price: Number(e.target.value) })}
            />
            <p className="mt-2 px-1 text-sm text-hint">{t('order.cashNote')}</p>
          </Field>
        </>
      );
      break;
    }

    case 'confirm':
      body = (
        <>
          <div className="card glow mb-4 p-5">
            <p className="text-xs font-bold tracking-wider text-muted uppercase">
              {t('order.route')}
            </p>
            <p className="mt-1 font-display text-xl leading-tight font-bold tracking-tight">
              {cityName(from!, lang)} <span className="text-accent">→</span> {cityName(to!, lang)}
            </p>
            <p className="mt-3 font-display text-2xl font-bold">
              {formatPrice(draft.price ?? 0, t('common.currency'))}
            </p>
          </div>
          <Section>
            <Row label={t('order.pickupShort')} value={draft.pickup?.address} />
            <Row
              label={t('order.when')}
              value={windowIso ? formatWindow(windowIso.from, windowIso.to, lang) : ''}
            />
            <Row label={t('order.dropoffShort')} value={draft.dropoff?.address} />
            <Row
              label={t('order.recipient')}
              value={`${draft.recipientName}, +998 ${draft.recipientPhone}`}
            />
            <Row
              label={t('order.item')}
              value={`${t(`category.${draft.category}`)} · ${t(`size.${draft.sizeClass}.title`)}`}
            />
          </Section>
          <label className="card flex items-start gap-3 p-5">
            <input
              type="checkbox"
              className="mt-0.5 h-5 w-5 shrink-0 accent-[var(--lime)]"
              checked={draft.confirmed}
              onChange={(e) => draft.set({ confirmed: e.target.checked })}
            />
            <span className="text-sm">
              {t('order.confirmProhibited')}{' '}
              <Link to="/rules" className="text-link">
                {t('order.seeRules')}
              </Link>
            </span>
          </label>
          {create.isError && (
            <CreateErrors
              error={create.error}
              fallback={errorMessage(create.error)}
              onFix={(step) => draft.set({ step })}
            />
          )}
        </>
      );
      break;
  }

  return (
    <>
      <div className="mb-3 flex items-center justify-between px-1">
        <h1 className="font-display text-xl font-bold tracking-tight">{t(`order.step.${step}`)}</h1>
        <span className="rounded-full border border-line px-2.5 py-1 font-display text-xs font-bold text-muted">
          {draft.step + 1}/{STEPS.length}
        </span>
      </div>
      <div className="mb-5 flex gap-1.5">
        {STEPS.map((s, i) => (
          <button
            key={s}
            type="button"
            aria-label={t(`order.step.${s}`)}
            disabled={i >= draft.step}
            onClick={() => draft.set({ step: i })}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              i < draft.step
                ? 'bg-lime'
                : i === draft.step
                  ? 'bg-lime shadow-[0_0_10px_var(--glow)]'
                  : 'bg-line'
            }`}
          />
        ))}
      </div>
      <div key={step} className="animate-fade-up pb-20">
        {body}
        <MissingList items={missing} />
      </div>
      {draft.step > 0 && (
        <button
          type="button"
          className="mb-24 w-full py-2 text-center text-sm font-semibold text-muted"
          onClick={() => draft.set({ step: draft.step - 1 })}
        >
          ← {t('common.back')}
        </button>
      )}
      {draft.step === 0 && (
        <button
          type="button"
          className="mb-24 w-full py-2 text-center text-sm font-semibold text-muted"
          onClick={() => {
            draft.reset();
            void navigate('/');
          }}
        >
          {t('order.discard')}
        </button>
      )}
      <MainAction
        text={step === 'confirm' ? t('order.create') : t('common.next')}
        onClick={next}
        enabled={valid[step]}
        loading={create.isPending}
      />
    </>
  );
}

function SuccessStep({ result }: { result: CreateOrderResponse }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const { order, deliveryCode } = result;

  const shareText = t('order.shareText', {
    number: order.publicNumber,
    code: deliveryCode,
  });

  const copy = async () => {
    try {
      // SDK o'zi Telegram → navigator.clipboard → execCommand fallback'larini sinaydi.
      await copyTextToClipboard(deliveryCode);
      haptic.success();
      setCopied(true);
    } catch {
      haptic.error();
    }
  };

  const share = () => {
    const url = `https://t.me/${import.meta.env.VITE_BOT_USERNAME}`;
    if (shareURL.isAvailable()) shareURL(url, shareText);
    else
      window.open(
        `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`,
      );
  };

  return (
    <div className="flex animate-fade-up flex-col items-center pt-6 text-center">
      <span className="mb-4 grid h-20 w-20 place-items-center rounded-full bg-lime text-4xl font-bold text-lime-ink shadow-[0_0_0_10px_var(--glow)]">
        ✓
      </span>
      <h1 className="mb-1 font-display text-xl font-bold tracking-tight">{t('order.created')}</h1>
      <p className="mb-6 font-mono text-sm text-muted">{order.publicNumber}</p>
      <div className="card glow mb-6 w-full p-5">
        <p className="mb-3 text-sm text-muted">{t('order.codeTitle')}</p>
        <div className="mb-3 flex justify-center gap-2">
          {deliveryCode.split('').map((d, i) => (
            <span
              key={i}
              className="grid h-16 w-13 place-items-center rounded-2xl border border-line bg-surface-2 font-display text-3xl font-bold"
            >
              {d}
            </span>
          ))}
        </div>
        <p className="mx-auto max-w-xs text-sm text-muted">{t('order.codeHint')}</p>
      </div>
      <div className="mb-8 flex w-full gap-2">
        <button type="button" className="btn-secondary flex-1" onClick={() => void copy()}>
          {copied ? `✓ ${t('order.copied')}` : t('order.copy')}
        </button>
        <button type="button" className="btn-secondary flex-1" onClick={share}>
          {t('order.shareToRecipient')}
        </button>
      </div>
      <MainAction
        text={t('order.openOrder')}
        onClick={() => void navigate(`/orders/${order.id}`, { replace: true })}
      />
    </div>
  );
}

/** API maydon/xato kodi → forma qadami (xatoni ko'rsatib, o'sha qadamga qaytarish uchun). */
const FIELD_STEP: Record<string, number> = {
  fromCityId: 0,
  toCityId: 0,
  pickupAddress: 1,
  pickupLat: 1,
  pickupLng: 1,
  pickupFrom: 1,
  pickupTo: 1,
  dropoffAddress: 2,
  dropoffLat: 2,
  dropoffLng: 2,
  recipientName: 2,
  recipientPhone: 2,
  category: 3,
  sizeClass: 3,
  description: 3,
  photoKeys: 3,
  price: 4,
  noProhibitedItems: 5,
};
const CODE_FIELD: Record<string, string> = {
  PRICE_OUT_OF_RANGE: 'price',
  PICKUP_TIME_INVALID: 'pickupFrom',
  UPLOAD_INVALID: 'photoKeys',
  CITY_INVALID: 'toCityId',
  ROUTE_NOT_SUPPORTED: 'toCityId',
  PHONE_REQUIRED: '',
};

function CreateErrors({
  error,
  fallback,
  onFix,
}: {
  error: unknown;
  fallback: string;
  onFix: (step: number) => void;
}) {
  const { t } = useTranslation();
  let fields: string[] = [];
  if (error instanceof ApiError) {
    if (error.code === 'VALIDATION_ERROR' && Array.isArray(error.details)) {
      fields = (error.details as { path: string }[]).map((d) => d.path.split('.')[0] ?? '');
    } else if (CODE_FIELD[error.code]) {
      fields = [CODE_FIELD[error.code]!];
    }
  }
  const unique = [...new Set(fields)].filter((f) => f in FIELD_STEP);

  return (
    <div className="card mt-3 border-danger/40 p-4 text-sm">
      <p className="mb-1 font-semibold text-destructive">⚠️ {fallback}</p>
      {unique.map((f) => (
        <button
          key={f}
          type="button"
          className="flex w-full items-center justify-between py-1.5 text-left"
          onClick={() => onFix(FIELD_STEP[f]!)}
        >
          <span>
            {t(`order.step.${STEPS[FIELD_STEP[f]!]}`)}: <b>{t(`order.field.${f}`)}</b>
          </span>
          <span className="text-link">{t('order.fix')} →</span>
        </button>
      ))}
    </div>
  );
}
